import React from "react";
import Link from "next/link";
import { signIn } from "next-auth/react";
import { useRouter } from "next/router";
import Footer from "../components/layout/Footer";
import PasswordInput from "@/components/ui/PasswordInput";
import { useI18n } from "@/i18n";

export default function LoginPage() {
  const { t } = useI18n();
  const router = useRouter();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!router.isReady) return;
    if (router.query.reason === "session_expired") {
      const message =
        typeof router.query.message === "string"
          ? router.query.message.trim()
          : "";
      setErr(message || t("auth.sessionExpired"));
    }
  }, [router.isReady, router.query.reason, router.query.message, t]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const res = await signIn("credentials", {
      redirect: false,
      email,
      password,
    });
    setBusy(false);
    if (res?.error) {
      if (res.error === "CredentialsSignin") {
        setErr(t("auth.invalidCredentials"));
      } else if (res.error === "AccountNotApproved") {
        setErr(t("auth.accountNotApproved"));
      } else {
        setErr(t("auth.loginFailed"));
      }
    } else {
      const to =
        (typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).get("callbackUrl")) ||
        "/";
      router.push(to);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-center">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/login-hero.png')" }}
      ></div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 gradient-light dark:gradient-dark opacity-80"></div>

      {/* Login card */}
      <div className="relative z-10 flex-grow flex items-center justify-center">
        <div className="bg-white dark:bg-neutral rounded-xl shadow-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold mb-6 text-center text-primary dark:text-secondary">
            {t("auth.signInTitle")}
          </h1>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <input
              type="email"
              placeholder={t("auth.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
            <PasswordInput
              placeholder={t("auth.passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
            {err && (
              <p className="mt-2 text-sm text-red-600">{err}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="bg-primary text-white p-3 rounded btn-hover btn-active"
            >
              {busy ? t("auth.signingIn") : t("auth.loginButton")}
            </button>
            <div className="text-center">
              <Link
                href="/auth/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
